import { useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { Papers } from './supabase';
import { createSubscriptionProvider } from './createSubscriptionProvider';
import { useSession } from './SessionContext';

// Create the provider and hook using the factory
const { SubscriptionProvider: BaseProvider, useSubscription } = createSubscriptionProvider(
    supabase,
    'Global Subscription'
);

interface SubscriptionProviderProps {
    children: ReactNode;
}

// Export the same types for backward compatibility
export type { SubscriptionStatus, SubscriptionError } from './createSubscriptionProvider';

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
    const queryClient = useQueryClient();

    return (
        <BaseProvider>
            <SubscriptionSetup queryClient={queryClient}>
                {children}
            </SubscriptionSetup>
        </BaseProvider>
    );
}

// Separate component to use the subscription context
function SubscriptionSetup({ children, queryClient }: { children: ReactNode; queryClient: any }) {
    const { setupSubscription } = useSubscription();
    const { session } = useSession();

    useEffect(() => {
        const initializeSubscription = async () => {
            if (!session?.user?.id) {
                console.log('🔐 [Global Subscription] No session available for subscription setup');
                return;
            }

            const userId = session.user.id;
            console.log('🔐 [Global Subscription] Setting up subscription for user:', userId);

            await setupSubscription({
                channelName: `global_papers_changes_${userId}`,
                subscriptions: [
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'papers',
                        callback: (payload) => {
                            const newPaper = payload.new as Papers;
                            console.log('📬 [Realtime] New paper insert detected:', newPaper.id);

                            if (newPaper.user_id === userId) {
                                console.log('📬 [Realtime] Invalidating user papers query to show pending item.');
                                queryClient.invalidateQueries({ queryKey: ['papers', 'user', newPaper.user_id] });
                            }
                        }
                    },
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'papers',
                        callback: (payload) => {
                            const updatedPaper = payload.new as Papers;
                            const oldPaper = payload.old as any;

                            console.log('📬 [Realtime] Paper update detected:', {
                                id: updatedPaper.id,
                                oldStatus: oldPaper?.status,
                                newStatus: updatedPaper.status,
                                isPrivate: updatedPaper.is_private,
                                userId: updatedPaper.user_id,
                            });

                            if (updatedPaper.user_id === userId) {
                                console.log('📬 [Realtime] Optimistically updating user papers query cache.');
                                queryClient.setQueryData(
                                    ['papers', 'user', updatedPaper.user_id],
                                    (oldData: Papers[] | undefined) => {
                                        if (!oldData) return [];
                                        return oldData.map((paper) =>
                                            paper.id === updatedPaper.id ? updatedPaper : paper
                                        );
                                    }
                                );
                            }
                        }
                    }
                ]
            });
        };

        // Initialize subscription when session changes
        initializeSubscription();
    }, [session?.user?.id, setupSubscription, queryClient]);

    return <>{children}</>;
}

export { useSubscription };